/**
 * Procedural signage. Owned by `world`.
 *
 * Every sign in the city — dépanneur fascias, projecting blade signs, ARRÊT octagons, the
 * incomprehensible Ville de Montréal parking panels, street-name blades, orange TRAVAUX
 * diamonds and faded ghost lettering — is drawn once onto a single 2048² canvas atlas and
 * then referenced by UV rect. That keeps ALL signage in the city at two draw calls: one
 * opaque/alpha-tested pass and one emissive pass for night.
 *
 * N1: nothing loaded. Canvas 2D + system-generic font stacks only.
 */

import * as THREE from 'three';
import { SHOPS, BLADE_WORDS, PARKING_SIGNS, NS_NAMES, EW_NAMES } from './names.js';

const SANS = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const HEAVY = '"Arial Black", "Helvetica Neue", Impact, sans-serif';
const SERIF = 'Georgia, "Times New Roman", serif';

export class SignAtlas {
  constructor(T, rng) {
    this.T = T;
    this.rng = rng;
    this.size = T.signs.atlas;
    this.rects = new Map();
    this._cx = 2; this._cy = 2; this._rowH = 0;
    this.texture = null;
  }

  _alloc(key, w, h) {
    if (this._cx + w + 2 > this.size) { this._cx = 2; this._cy += this._rowH + 2; this._rowH = 0; }
    const r = { x: this._cx, y: this._cy, w, h, aspect: w / h };
    this._cx += w + 2;
    if (h > this._rowH) this._rowH = h;
    r.u0 = r.x / this.size;
    r.u1 = (r.x + r.w) / this.size;
    r.v0 = 1 - (r.y + r.h) / this.size;
    r.v1 = 1 - r.y / this.size;
    this.rects.set(key, r);
    return r;
  }

  uv(key) { return this.rects.get(key); }

  build() {
    const c = document.createElement('canvas');
    c.width = c.height = this.size;
    this.canvas = c;
    this._paint();

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    tex.name = 'world:signAtlas';
    this.texture = tex;
    return tex;
  }

  /**
   * Repaint the SAME canvas on a rebuild. The UV layout is a pure function of the tuning table
   * and is therefore unchanged; only the rng-driven weathering differs. Keeping the texture
   * object identical is what lets the look-driven material variants stay cached (world.js
   * `_build`) and keeps a 16 MB canvas out of the per-reset heap (§7.2b).
   */
  redraw(rng) {
    this.rng = rng;
    this.rects.clear();
    this._cx = 2; this._cy = 2; this._rowH = 0;
    this._paint();
    if (this.texture) this.texture.needsUpdate = true;
    return this.texture;
  }

  _paint() {
    const c = this.canvas;
    const g = c.getContext('2d', { alpha: true });
    g.clearRect(0, 0, this.size, this.size);
    g.textBaseline = 'middle';
    g.textAlign = 'center';

    const S = this.T.signs;

    // ---- shop fascia boards -------------------------------------------------
    for (let i = 0; i < SHOPS.length; i++) {
      const s = SHOPS[i];
      const r = this._alloc('fascia:' + i, S.fasciaW, S.fasciaH);
      this._fascia(g, r, s);
    }

    // ---- projecting blade signs (letters stacked vertically) ----------------
    for (let i = 0; i < BLADE_WORDS.length; i++) {
      const r = this._alloc('blade:' + i, S.bladeW, S.bladeH);
      this._blade(g, r, BLADE_WORDS[i]);
    }

    // ---- ARRÊT --------------------------------------------------------------
    this._arret(g, this._alloc('arret', S.plateW, S.plateH));

    // ---- parking panels -----------------------------------------------------
    for (let i = 0; i < PARKING_SIGNS.length; i++) {
      this._parking(g, this._alloc('park:' + i, S.parkW, S.parkH), PARKING_SIGNS[i]);
    }

    // ---- roadworks ----------------------------------------------------------
    this._works(g, this._alloc('works:0', 168, 168), 'TRAVAUX', true);
    this._works(g, this._alloc('works:1', 168, 168), 'DÉTOUR', true);
    this._worksBar(g, this._alloc('works:2', 384, 120), 'ROUTE BARRÉE');
    this._worksBar(g, this._alloc('works:3', 384, 120), 'CHANTIER · VILLE DE MONTRÉAL');

    // ---- street name blades -------------------------------------------------
    const names = NS_NAMES.concat(EW_NAMES);
    for (let i = 0; i < names.length; i++) {
      this._streetBlade(g, this._alloc('street:' + i, S.streetW, S.streetH), names[i]);
    }

    // ---- ghost lettering on gable-end walls ---------------------------------
    const ghosts = ['BIÈRE · VIN', 'TABAGIE', 'CHAUSSURES', 'HÔTEL', 'PHARMACIE'];
    for (let i = 0; i < ghosts.length; i++) {
      this._ghost(g, this._alloc('ghost:' + i, 384, 256), ghosts[i]);
    }

    // ---- lit window lettering (night) ---------------------------------------
    const lit = ['OUVERT', 'DÉPANNEUR', 'BIÈRE FROIDE', 'LOTERIE'];
    for (let i = 0; i < lit.length; i++) {
      this._neon(g, this._alloc('neon:' + i, 320, 96), lit[i]);
    }
  }

  // ------------------------------------------------------------------ drawing

  _fit(g, text, maxW, px, family, weight) {
    let size = px;
    for (let k = 0; k < 24; k++) {
      g.font = `${weight} ${size}px ${family}`;
      if (g.measureText(text).width <= maxW) break;
      size -= 2;
      if (size < 7) break;
    }
    return size;
  }

  _fascia(g, r, s) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.fillStyle = hex(s.tint);
    g.fillRect(0, 0, w, h);
    // inset border
    g.strokeStyle = 'rgba(0,0,0,0.35)';
    g.lineWidth = 3;
    g.strokeRect(4.5, 4.5, w - 9, h - 9);
    g.strokeStyle = 'rgba(255,255,255,0.20)';
    g.lineWidth = 2;
    g.strokeRect(9, 9, w - 18, h - 18);
    // grime along the bottom edge
    const grad = g.createLinearGradient(0, h * 0.6, 0, h);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.22)');
    g.fillStyle = grad;
    g.fillRect(0, 0, w, h);

    /**
     * ROUND-11. `wide-establish`: "Signage is blank planes." The art was there; it was too
     * small to survive the distance. The 512×104 cell was giving the shop name a 46 px cap —
     * 44 % of the board, so 0.48 m of a 1.10 m fascia, which at the shot's pose (64 m, 40°
     * vertical fov, 1080 px ⇒ 23 px/m) is an 11 px cap. Round 7 sized the BOARD for a 14 px cap
     * and then set the type at two thirds of that.
     *
     * The name now takes 65 % of the cell (≈ 0.72 m ⇒ ~16 px, inside the 14-20 px band the
     * critic asked for) and the strapline drops to a thin bottom strip. `l1` is where every
     * accented character lives — DÉPANNEUR, CASSE-CROÛTE, FRUITERIE — so `wide-establish`
     * criterion 6 is carried by the line that got bigger, not the one that got smaller.
     */
    g.fillStyle = hex(s.ink);
    const s1 = this._fit(g, s.l1, w - 26, 68, HEAVY, '900');
    g.font = `900 ${s1}px ${HEAVY}`;
    g.fillText(s.l1, w / 2, h * 0.66);
    const s2 = this._fit(g, s.l2, w - 40, 15, SANS, '700');
    g.font = `700 ${s2}px ${SANS}`;
    g.globalAlpha = 0.9;
    g.fillText(s.l2, w / 2, h * 0.92);
    g.globalAlpha = 1;
    g.restore();
  }

  _blade(g, r, b) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.fillStyle = hex(b.tint);
    g.beginPath();
    roundRect(g, 3, 3, w - 6, h - 6, 8);
    g.fill();
    g.strokeStyle = 'rgba(0,0,0,0.45)';
    g.lineWidth = 3;
    g.stroke();
    g.fillStyle = hex(b.ink);
    const letters = Array.from(b.t);
    const step = (h - 24) / letters.length;
    const px = Math.min(step * 0.86, w * 0.72);
    g.font = `900 ${px}px ${HEAVY}`;
    for (let i = 0; i < letters.length; i++) {
      g.fillText(letters[i], w / 2, 14 + step * (i + 0.5));
    }
    g.restore();
  }

  _arret(g, r) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x + w / 2, y + h / 2);
    const R = w * 0.48;
    g.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const px = Math.cos(a) * R, py = Math.sin(a) * R;
      if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
    }
    g.closePath();
    g.fillStyle = '#c1121f';
    g.fill();
    g.lineWidth = w * 0.045;
    g.strokeStyle = '#ffffff';
    g.stroke();
    g.fillStyle = '#ffffff';
    const px = this._fit(g, 'ARRÊT', w * 0.76, w * 0.30, HEAVY, '900');
    g.font = `900 ${px}px ${HEAVY}`;
    g.fillText('ARRÊT', 0, 2);
    g.restore();
  }

  _parking(g, r, lines) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.fillStyle = '#f2f1ec';
    g.fillRect(0, 0, w, h);
    g.strokeStyle = '#2a2a2a';
    g.lineWidth = 4;
    g.strokeRect(2, 2, w - 4, h - 4);
    const head = lines[0];
    if (head === 'P') {
      g.fillStyle = '#1a7a3c';
      g.font = `900 ${w * 0.60}px ${HEAVY}`;
      g.fillText('P', w / 2, h * 0.17);
    } else {
      g.strokeStyle = '#c1121f';
      g.lineWidth = w * 0.085;
      g.beginPath();
      g.arc(w / 2, h * 0.17, w * 0.27, 0, Math.PI * 2);
      g.stroke();
      g.beginPath();
      g.moveTo(w / 2 - w * 0.19, h * 0.17 - w * 0.19);
      g.lineTo(w / 2 + w * 0.19, h * 0.17 + w * 0.19);
      g.stroke();
    }
    g.fillStyle = '#17171a';
    for (let i = 1; i < lines.length; i++) {
      const px = this._fit(g, lines[i], w - 14, 22, SANS, '700');
      g.font = `700 ${px}px ${SANS}`;
      g.fillText(lines[i], w / 2, h * (0.40 + 0.15 * (i - 1)));
    }
    // the arrow every Montrealer squints at
    g.strokeStyle = '#17171a';
    g.lineWidth = 5;
    g.beginPath();
    g.moveTo(w * 0.22, h * 0.90); g.lineTo(w * 0.78, h * 0.90);
    g.moveTo(w * 0.78, h * 0.90); g.lineTo(w * 0.66, h * 0.83);
    g.moveTo(w * 0.78, h * 0.90); g.lineTo(w * 0.66, h * 0.97);
    g.stroke();
    g.restore();
  }

  _works(g, r, text, diamond) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x + w / 2, y + h / 2);
    if (diamond) g.rotate(Math.PI / 4);
    g.fillStyle = '#f26a1b';
    const s = diamond ? w * 0.68 : w;
    g.fillRect(-s / 2, -s / 2, s, s);
    g.strokeStyle = '#1c1c1c';
    g.lineWidth = 5;
    g.strokeRect(-s / 2 + 4, -s / 2 + 4, s - 8, s - 8);
    if (diamond) g.rotate(-Math.PI / 4);
    g.fillStyle = '#17171a';
    const px = this._fit(g, text, w * 0.80, 30, HEAVY, '900');
    g.font = `900 ${px}px ${HEAVY}`;
    g.fillText(text, 0, 2);
    g.restore();
  }

  _worksBar(g, r, text) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.fillStyle = '#f26a1b';
    g.fillRect(0, 0, w, h);
    g.fillStyle = '#f5f3ee';
    for (let i = -1; i < w / 26 + 1; i++) {
      g.beginPath();
      g.moveTo(i * 26, h); g.lineTo(i * 26 + 13, h); g.lineTo(i * 26 + 13 + h * 0.5, 0); g.lineTo(i * 26 + h * 0.5, 0);
      g.closePath(); g.fill();
    }
    g.fillStyle = 'rgba(20,20,20,0.86)';
    g.fillRect(0, h * 0.28, w, h * 0.44);
    g.fillStyle = '#f7f4ec';
    const px = this._fit(g, text, w - 24, 34, HEAVY, '900');
    g.font = `900 ${px}px ${HEAVY}`;
    g.fillText(text, w / 2, h * 0.50);
    g.restore();
  }

  _streetBlade(g, r, name) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.fillStyle = '#0f4d3d';
    g.beginPath();
    roundRect(g, 1, 1, w - 2, h - 2, h * 0.22);
    g.fill();
    g.strokeStyle = '#f3f1e8';
    g.lineWidth = 3;
    g.stroke();
    g.fillStyle = '#f6f4ec';
    const px = this._fit(g, name, w - 24, h * 0.52, SANS, '700');
    g.font = `700 ${px}px ${SANS}`;
    g.fillText(name, w / 2, h * 0.54);
    g.restore();
  }

  _ghost(g, r, text) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.globalAlpha = 0.30;
    g.fillStyle = '#efe6d4';
    const px = this._fit(g, text, w - 20, 72, SERIF, '700');
    g.font = `700 ${px}px ${SERIF}`;
    g.fillText(text, w / 2, h * 0.5);
    // eat it away so it reads as a century of weather
    g.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 260; i++) {
      const rx = this.rng.range(0, w), ry = this.rng.range(0, h);
      const rr = this.rng.range(2, 13);
      g.globalAlpha = this.rng.range(0.15, 0.75);
      g.beginPath(); g.arc(rx, ry, rr, 0, Math.PI * 2); g.fill();
    }
    g.globalCompositeOperation = 'source-over';
    g.globalAlpha = 1;
    g.restore();
  }

  _neon(g, r, text) {
    const { x, y, w, h } = r;
    g.save();
    g.translate(x, y);
    g.fillStyle = '#12060a';
    g.fillRect(0, 0, w, h);
    g.shadowColor = '#ff3b5c';
    g.shadowBlur = 18;
    g.strokeStyle = '#ff6f88';
    g.lineWidth = 4;
    const px = this._fit(g, text, w - 26, h * 0.62, SANS, '700');
    g.font = `700 ${px}px ${SANS}`;
    g.strokeText(text, w / 2, h * 0.53);
    g.shadowBlur = 6;
    g.fillStyle = '#ffe3ea';
    g.fillText(text, w / 2, h * 0.53);
    g.shadowBlur = 0;
    g.restore();
  }
}

function hex(v) {
  return '#' + (v & 0xffffff).toString(16).padStart(6, '0');
}

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.lineTo(x + w - r, y); g.quadraticCurveTo(x + w, y, x + w, y + r);
  g.lineTo(x + w, y + h - r); g.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  g.lineTo(x + r, y + h); g.quadraticCurveTo(x, y + h, x, y + h - r);
  g.lineTo(x, y + r); g.quadraticCurveTo(x, y, x + r, y);
  g.closePath();
}

/**
 * Emit a sign quad into a MeshBuilder with the atlas UVs.
 * `p` is the centre, `right`/`up` are the half-extent vectors of the panel.
 */
export function signQuad(mb, rect, cx, cy, cz, rx, ry, rz, ux, uy, uz, twoSided) {
  const a = [cx - rx - ux, cy - ry - uy, cz - rz - uz];
  const b = [cx + rx - ux, cy + ry - uy, cz + rz - uz];
  const c = [cx + rx + ux, cy + ry + uy, cz + rz + uz];
  const d = [cx - rx + ux, cy - ry + uy, cz - rz + uz];
  const uv = [[rect.u0, rect.v0], [rect.u1, rect.v0], [rect.u1, rect.v1], [rect.u0, rect.v1]];
  mb.quad(a, b, c, d, uv);
  if (twoSided) {
    /**
     * MIRRORED GLYPHS — round-6 `night-rain` major: "the POUTINE sign texture is horizontally
     * mirrored; it renders as reversed glyphs in a hero frame."
     *
     * The back face is wound b,a,d,c so it faces the other way. The old back UVs were
     * `[u1,v0],[u0,v0],[u0,v1],[u1,v1]`, which gives vertex `b` the SAME u it has on the front
     * — i.e. the texture was pinned identically in WORLD space on both faces. Painting a sign
     * that way is what a decal does, and a decal read from behind is mirrored. A double-sided
     * shop sign is painted twice, once per side, so the back face has to run its u the other
     * way round the quad. Reusing `uv` unchanged against the reversed winding does exactly
     * that. Every twoSided caller — blade signs, ARRÊT octagons, street blades and the Ville
     * de Montréal parking panels — was carrying the same bug.
     */
    mb.quad(b, a, d, c, uv);
  }
}
